import * as React from 'react';
import { useQueries } from '@tanstack/react-query';
import { Select } from '@/components/ui/Select';
import { fxRateKeys } from '@/hooks/useFxRates';
import { FxRateService } from '@/services/FxRateService';
import { cn, formatCurrency } from '@/lib/utils';
import type { Budget } from '@/types';

type Operation = 'sum' | 'avg' | 'subtract';
type Metric = 'totalBudget' | 'directSpending' | 'allocated' | 'unallocated';

interface SelectionSummaryRibbonProps {
  selectedBudgets: Budget[];
  onExit: () => void;
}

function metricValue(budget: Budget, metric: Metric): number {
  switch (metric) {
    case 'totalBudget':
      return budget.amount;
    case 'directSpending':
      return budget.directSpent ?? budget.spent ?? 0;
    case 'allocated':
      return budget.allocated ?? 0;
    case 'unallocated':
      return budget.unallocated ?? 0;
  }
}

export function SelectionSummaryRibbon({ selectedBudgets, onExit }: SelectionSummaryRibbonProps) {
  const [operation, setOperation] = React.useState<Operation>('sum');
  const [metric, setMetric] = React.useState<Metric>('totalBudget');

  const count = selectedBudgets.length;
  const targetCurrency = selectedBudgets[0]?.currency ?? '';
  const canSubtract = count === 2;

  // Reset Subtract → Sum when selection leaves exactly two
  React.useEffect(() => {
    if (operation === 'subtract' && !canSubtract) {
      setOperation('sum');
    }
  }, [operation, canSubtract]);

  const uniqueSourceCurrencies = React.useMemo(() => {
    if (!targetCurrency) return [] as string[];
    const currencies = new Set(
      selectedBudgets.map((b) => b.currency).filter((c) => c !== targetCurrency)
    );
    return Array.from(currencies);
  }, [selectedBudgets, targetCurrency]);

  const rateQueries = useQueries({
    queries: uniqueSourceCurrencies.map((from) => ({
      queryKey: fxRateKeys.rate(from, targetCurrency),
      queryFn: () => FxRateService.getRate(from, targetCurrency),
      staleTime: 60 * 60 * 1000,
      enabled: count > 0 && !!from && !!targetCurrency && from !== targetCurrency,
    })),
  });

  const ratesByFrom = React.useMemo(() => {
    const map = new Map<string, number | null>();
    uniqueSourceCurrencies.forEach((from, index) => {
      map.set(from, rateQueries[index]?.data ?? null);
    });
    return map;
  }, [uniqueSourceCurrencies, rateQueries]);

  const isRatesLoading = rateQueries.some((q) => q.isLoading || q.isFetching);
  const hasRateError = uniqueSourceCurrencies.some((from, index) => {
    const query = rateQueries[index];
    if (query?.isError) return true;
    if (!query?.isLoading && !query?.isFetching && ratesByFrom.get(from) == null) return true;
    return false;
  });

  const convertedValues = React.useMemo(() => {
    if (count === 0 || isRatesLoading || hasRateError) return null;
    return selectedBudgets.map((budget) => {
      const raw = metricValue(budget, metric);
      if (budget.currency === targetCurrency) return raw;
      const rate = ratesByFrom.get(budget.currency);
      if (rate == null) return null;
      return raw * rate;
    });
  }, [
    count,
    selectedBudgets,
    metric,
    targetCurrency,
    ratesByFrom,
    isRatesLoading,
    hasRateError,
  ]);

  const result = React.useMemo(() => {
    if (!convertedValues || convertedValues.some((v) => v == null)) return null;
    const values = convertedValues as number[];
    if (operation === 'sum') {
      return values.reduce((acc, v) => acc + v, 0);
    }
    if (operation === 'avg') {
      return values.reduce((acc, v) => acc + v, 0) / values.length;
    }
    return values[0]! - values[1]!;
  }, [convertedValues, operation]);

  if (count === 0) return null;

  const operationOptions = [
    { value: 'sum', label: 'Sum' },
    { value: 'avg', label: 'Average' },
    ...(canSubtract ? [{ value: 'subtract', label: 'Subtract' }] : []),
  ];

  const metricOptions = [
    { value: 'totalBudget', label: 'Total Budget' },
    { value: 'directSpending', label: 'Direct Spending' },
    { value: 'allocated', label: 'Allocated' },
    { value: 'unallocated', label: 'Unallocated' },
  ];

  const subtractLabel =
    canSubtract && operation === 'subtract'
      ? `${selectedBudgets[0]!.name} − ${selectedBudgets[1]!.name}`
      : null;

  let resultDisplay: React.ReactNode;
  if (isRatesLoading) {
    resultDisplay = <span className="text-muted-foreground">…</span>;
  } else if (hasRateError) {
    resultDisplay = (
      <span className="text-destructive text-xs">FX rate unavailable</span>
    );
  } else if (result != null) {
    resultDisplay = (
      <span className="font-semibold text-foreground tabular-nums">
        {formatCurrency(result, targetCurrency)}
      </span>
    );
  } else {
    resultDisplay = <span className="text-muted-foreground">—</span>;
  }

  return (
    <div
      data-testid="selection-ops-panel"
      className="fixed bottom-20 right-4 md:bottom-6 z-50 rounded-xl border border-border bg-background/95 backdrop-blur shadow-xl p-3 min-w-[260px] max-w-sm"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          {count} budget{count !== 1 ? 's' : ''} selected
        </span>
        <button
          type="button"
          onClick={onExit}
          className="ml-2 rounded-full p-0.5 text-muted-foreground hover:text-foreground hover:bg-muted"
          aria-label="Exit selection"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-end gap-2">
        <div className="flex-1 min-w-0">
          <Select
            label="Operation"
            options={operationOptions}
            value={operation}
            onChange={(e) => setOperation(e.target.value as Operation)}
            className="h-9"
          />
        </div>
        <div className="flex-1 min-w-0">
          <Select
            label="Value"
            options={metricOptions}
            value={metric}
            onChange={(e) => setMetric(e.target.value as Metric)}
            className="h-9"
          />
        </div>
        <div className="sm:pb-1 sm:pl-1 sm:min-w-[7rem] text-right">
          <p className="text-xs text-muted-foreground mb-1 sm:hidden">Result</p>
          <div className={cn('text-sm')}>{resultDisplay}</div>
        </div>
      </div>

      {subtractLabel && (
        <p className="mt-2 text-[11px] text-muted-foreground truncate" title={subtractLabel}>
          {subtractLabel}
        </p>
      )}
      {uniqueSourceCurrencies.length > 0 && !hasRateError && (
        <p className="mt-1 text-[11px] text-muted-foreground">
          Converted to {targetCurrency}
        </p>
      )}
    </div>
  );
}
